import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, Award } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets-4nw71qhi.emergentagent.net/job_7e4e61dd-6912-4af3-ac17-762c1ac12aa9/artifacts/35pjqkjb_TK6_2813.jpg";

export default function Hero() {
  const [stats, setStats] = useState({
    total_applications: 330,
    country_count: 60,
  });

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats(s => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  return (
    <section className="relative bg-ink overflow-hidden" data-testid="hero-section">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="GYP Delegates at the United Nations Conference Centre"
          className="w-full h-full object-cover object-[center_35%]"
        />
        {/* Gradient overlay for text legibility */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(to right, rgba(6,11,34,0.90) 0%, rgba(6,11,34,0.65) 50%, rgba(6,11,34,0.25) 100%)"
        }} />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(to top, rgba(6,11,34,0.70) 0%, transparent 45%)"
        }} />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="min-h-[92vh] flex flex-col justify-between py-16 lg:py-20">

          {/* Top pill */}
          <div className="pt-8 lg:pt-4">
            <div className="inline-flex items-center gap-3 border border-white/25 backdrop-blur-sm px-4 py-2 bg-white/5" data-testid="status-badge">
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#B8944D" }} />
              <span className="text-[10px] tracking-[0.35em] uppercase text-white/85">
                Barcelona 2027 · Applications Open
              </span>
            </div>
          </div>

          {/* Center content */}
          <div className="max-w-3xl">
            <p className="text-[11px] tracking-[0.4em] uppercase mb-6" style={{ color: "#B8944D" }}>
              Global Youth Platform
            </p>
            <h1
              className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] leading-[0.95] mb-8"
              data-testid="hero-title"
            >
              Where the next<br />
              generation of<br />
              <span style={{ color: "#B8944D", fontStyle: "italic", fontWeight: 300 }}>diplomats</span> rise.
            </h1>
            <p className="text-white/75 text-lg lg:text-xl leading-relaxed max-w-2xl mb-10 font-light" data-testid="hero-subtitle">
              An international organization convening young leaders at the world&apos;s most
              consequential institutions — the United Nations, and beyond.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <button
                  className="text-xs tracking-[0.25em] uppercase font-medium px-9 py-4 flex items-center gap-3 transition-all hover:gap-4"
                  style={{ backgroundColor: "#B8944D", color: "#0E1B3D" }}
                  data-testid="hero-apply-btn"
                >
                  Register for Barcelona 2027
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </Link>
              <Link to="/partner">
                <button
                  className="border border-white/30 hover:border-white text-white px-8 py-4 text-xs tracking-[0.2em] uppercase transition-colors hover:bg-white/5"
                  data-testid="hero-partner-btn"
                >
                  Partnership Inquiry
                </button>
              </Link>
            </div>
          </div>

          {/* Bottom trust strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 pt-10 mt-16 border-t border-white/15">
            <TrustItem icon={<MapPin />} label="Host Venue" value="UN Conference Centre" />
            <TrustItem icon={<Users />} label="Delegates" value="+300 Youth Leaders" />
            <TrustItem icon={<Award />} label="Represented" value="+60 Countries" />
            <TrustItem icon={<div className="h-4 w-4 rounded-full" style={{ background: "#B8944D" }} />} label="Est." value="International Organization" />
          </div>

        </div>
      </div>
    </section>
  );
}

const TrustItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-1 shrink-0 [&>svg]:h-4 [&>svg]:w-4" style={{ color: "#B8944D" }}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[9px] tracking-[0.3em] uppercase text-white/45 mb-1">{label}</p>
      <p className="text-sm text-white font-medium truncate">{value}</p>
    </div>
  </div>
);
